import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Inserisci email e password');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/chat');
    } catch (err) {
      setError(err.message || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col lg:flex-row" data-testid="login-page">
      {/* Brand panel */}
      <div className="lg:w-1/2 relative flex items-center justify-center py-12 lg:py-0 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] to-transparent" />
        <div className="relative z-10 text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_toyou-ai-white-label/artifacts/rulpv6xu_TOYOU_logo-payoff-bianco.png" 
            alt="TOYOU" 
            className="h-16 lg:h-24 mx-auto mb-4 lg:mb-8"
          />
          <p className="text-white text-sm font-medium tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
            {' · Your limitless assistant'}
          </p>
        </div>
      </div>

      {/* Login panel */}
      <div className="flex-1 flex items-center justify-center p-6 pb-12 lg:pb-6">
        <div className="w-full max-w-md">
          <Card className="bg-[#101013] border border-white/[0.08] shadow-[0_12px_40px_rgba(0,0,0,0.55)]">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6 sm:mb-8">
                <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Accedi</h1>
                <p className="text-white/50 text-sm mt-2">Inserisci le tue credenziali per continuare</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70 text-sm">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@toyou.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 focus:border-white/20 focus:ring-white/10"
                    data-testid="login-email-input"
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/70 text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 h-11 pr-10 focus:border-white/20 focus:ring-white/10"
                      data-testid="login-password-input"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2" data-testid="login-error">
                    {error}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-white text-black font-semibold hover:bg-white/90 transition-colors"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accesso in corso...</>
                  ) : (
                    'Accedi'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-white/20 text-xs mt-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <span className="font-medium">TO</span><span className="font-extrabold">YOU</span>
            {' · '}
            <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
          </p>
        </div>
      </div>
    </div>
  );
}
